/**
 * 🎣 **Floats on the water** — D129. One small ball per player fishing, bobbing
 * on the surface, and **ducking under when a fish bites** — the only moment in
 * fishing that asks anything of you.
 *
 * 🔴 **`MeshBasicMaterial`, the material the cracks and the ropes already use**
 * — nothing new has to compile (the blackout of 2026-09-01). Two instances, a
 * red top and nothing more: a float has to read at ten metres on grey water.
 */

import { Mesh, MeshBasicMaterial, SphereGeometry, type Scene } from 'three';

/** How big a float is drawn, metres across. */
const RADIUS = 0.09;
/** How far under it ducks on a bite, metres. */
const DIP = 0.18;

export interface FloatView {
  readonly by: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly dip: boolean;
}

/** 🫧 **Where a float is drawn this instant** — bobbing, or under. Exported for a test. */
export function floatHeight(surface: number, dip: boolean, seconds: number): number {
  if (dip) return surface - DIP + Math.sin(seconds * 18) * 0.02;
  return surface + 0.02 + Math.sin(seconds * 2.4) * 0.025;
}

export class Floats {
  private readonly scene: Scene;
  private readonly live = new Map<string, Mesh>();
  private readonly geometry = new SphereGeometry(RADIUS, 10, 8);
  /** 🔴 Built once, never mid-game. */
  private readonly material = new MeshBasicMaterial({ color: 0xc23b22 });

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Draw exactly these floats, in render space. Returns where each one is drawn
   * this frame, WORLD metres, so the line can end on it rather than beside it.
   */
  update(
    floats: readonly FloatView[],
    origin: { x: number; y: number; z: number },
    seconds: number,
  ): Map<string, { x: number; y: number; z: number }> {
    const drawn = new Map<string, { x: number; y: number; z: number }>();
    const seen = new Set<string>();
    for (const f of floats) {
      seen.add(f.by);
      let mesh = this.live.get(f.by);
      if (mesh === undefined) {
        mesh = new Mesh(this.geometry, this.material);
        this.scene.add(mesh);
        this.live.set(f.by, mesh);
      }
      const y = floatHeight(f.y, f.dip, seconds);
      mesh.position.set(f.x - origin.x, y - origin.y, f.z - origin.z);
      drawn.set(f.by, { x: f.x, y, z: f.z });
    }
    for (const [by, mesh] of this.live) {
      if (seen.has(by)) continue;
      this.scene.remove(mesh);
      this.live.delete(by);
    }
    return drawn;
  }
}
